import { useBreakpointMinLg } from '@kk/shared/hooks/useBreakpoint';
import type { PressEvent } from '@react-types/shared';
import clsx from 'clsx';
import type React from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { twMerge } from 'tailwind-merge';
import type { NotificationType } from '#ui/constants';
import { useTheme } from '#ui/hooks/useTheme.js';
import Icon, { type IconProps, type IconType } from '../Icon';
import { Spinner } from '../Spinner/Spinner';
import type { ButtonTheme as Theme } from './index';
import { type ButtonVariantProps, buttonVariants } from './styles';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'>,
    ButtonVariantProps {
  theme?: Theme;
  disabled?: boolean | undefined;
  children?: React.ReactNode;
  label?: string;
  icon?: IconType;
  iconProps?: Omit<IconProps, 'type'>;
  onPress?: (e: React.MouseEvent<HTMLButtonElement> | PressEvent) => void;
}

export const mapNotificationType = (type: NotificationType): Theme => {
  switch (type) {
    case 'success':
      return 'subtle';
    case 'default':
    case 'info':
      return 'primary';
    case 'danger':
      return 'danger';
    default:
      return type as Theme;
  }
};

export function Button(props: ButtonProps) {
  const isKunde = useTheme() === 'kunde';
  const isLaane = useTheme() === 'laane';
  const minLg = useBreakpointMinLg();

  const {
    theme = 'default',
    icon,
    compact = isKunde ? minLg : undefined, // default compact styling for sizes above tablet
    inverted = false,
    rightAligned,
    label,
    children,
    loading,
    disabled,
    className,
    onPress,
    onClick,
    iconProps,
    ...rest
  } = props;

  const buttonContent = (
    <>
      {icon ? (
        <Icon
          solidOnActive
          type={icon}
          size={compact ? 'tiny' : 'small'}
          {...iconProps}
          className={twMerge(
            label || children
              ? rightAligned
                ? compact
                  ? 'laane:ml-1 kunde:ml-2'
                  : 'laane:ml-2 kunde:ml-2'
                : compact
                  ? 'laane:mr-1 kunde:mr-2'
                  : 'laane:mr-2 kunde:mr-2'
              : '',
            iconProps?.className,
          )}
        />
      ) : null}
      {label ?? children}
    </>
  );

  return (
    <button
      className={twMerge(
        buttonVariants({
          theme,
          compact,
          rightAligned,
          loading,
          inverted,
          hasIcon: !!icon,
          hasLabel: !!(label || children),
        }),
        className,
      )}
      disabled={!!disabled}
      {...rest}
      onClick={(e) => {
        onPress?.(e);
        onClick?.(e);
      }}
    >
      {loading ? (
        <Spinner
          className={clsx(
            'flex items-center justify-center',
            compact ? 'laane:px-4' : 'laane:px-1',
          )}
          size={compact && isLaane ? 'small' : 'medium'}
        />
      ) : (
        buttonContent
      )}
    </button>
  );
}

export default Button;
